import { ReactComponent as Arrows } from '@/../../public/arrowsdark.svg';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Globe, GlobeLock } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../AuthProvider';
import '../template.module.css';
import { ReportTemplatesContext } from '..';
import { colors } from '../utils/colors';
import { BotSVG } from './HelperSVGs';

const theme = createTheme({
  palette: {
    primary: {
      main: '#9c27b0', // Purple
    },
    secondary: {
      main: '#3f51b5', // Indigo
    },
  },
});

function getColor(index) {
  return colors[index % colors.length];
}

export default function TemplateTableSection() {
  const {
    setCurrentStage,
    Maps,
    Sections,
    setSections,
    fetchMaps,
    getAccessToken,
  } = useContext(ReportTemplatesContext);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  async function getTemplates() {
    try {
      const user = JSON.parse(localStorage.getItem('mongodbUser'));
      if (!user) throw new Error('User not found in localstorage');

      const url =
        process.env.REACT_APP_NODE_URL + '/templates/' + user._id + '?option=1';
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',

          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      const data = await res.json();
      setTemplates(data);
      setTemplatesLoading(false);
    } catch (error) {
      console.error(error);
      setTemplatesLoading(false);
    }
  }

  async function reoraganizeSections() {
    if (Maps) {
      const updatedSections = Maps.map(map => {
        const sectionToUpdate = Sections.find(
          section => section.key === map.key,
        );
        if (sectionToUpdate) {
          return {
            ...sectionToUpdate,
            map: {
              ...map,
              image: map?.imageURL,
              status: false,
            },
          };
        } else {
          return {
            key: map.key,
            title: map.title,
            blocks: [],
            map: {
              ...map,
              image: map?.imageURL,
              status: false,
            },
          };
        }
      });
      setSections(updatedSections);
      return;
    } else {
      await fetchMaps();
    }
  }

  const fetchUser = async outsettaID => {
    try {
      const url = new URL(
        process.env.REACT_APP_NODE_URL + '/user_data/' + outsettaID,
      );
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      if (response.status === 404) {
        navigate('/in-progress');
      }

      const data = await response.json();
      const { user } = data;
      localStorage.setItem('mongodbUser', JSON.stringify(user));
      getTemplates();
    } catch (error) {}
  };

  useEffect(() => {
    const outsettaUserID = user?.Uid;
    if (outsettaUserID) {
      fetchUser(outsettaUserID);
      reoraganizeSections();
    }
  }, []);

  return (
    <Box
      sx={{
        paddingX: '1.25rem',
        minHeight: '30rem',
        border: '1px solid #EDF2F7',
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Title Bar (with Button) */}
      <Box
        sx={{
          height: '3.6rem',
          paddingY: '0.4rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Name and Arrow Keys */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '1rem',
              fontWeight: 800,
              lineHeight: '24px',
              letterSpacing: '0.20000000298023224px',
              textAlign: 'left',
              color: '#004852',
            }}
          >
            Name
          </Typography>
          <Arrows />
        </Box>

        {/* Add New Button */}
        <Button
          variant='contained'
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            height: '100%',
            width: '8.6rem',
            padding: '0.5rem 1.25rem 0.5rem 1.25rem',
            borderRadius: '0.75rem',
            backgroundColor: '#4FA490',
            '&:hover': {
              backgroundColor: '#4FA490',
            },
          }}
          onClick={async () => {
            setCurrentStage('createTemplate');
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontSize: '0.9rem',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.2px',
              textAlign: 'center',
              color: '#FFFFFF',
            }}
          >
            Add New
          </Typography>
        </Button>
      </Box>

      {/* Template Info */}
      <Box>
        {templatesLoading &&
          [...Array(5)].map((_, i) => (
            <Skeleton
              key={i}
              variant='rounded'
              sx={{
                height: '2.5rem',
                borderTop: '1px solid #EDF2F7',
                marginTop: '0.5rem',
              }}
            />
          ))}

        {templates.length === 0 && !templatesLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '2rem',
            }}
          >
            <BotSVG />
            <Typography
              sx={{
                fontFamily: 'Lato',
                fontSize: '1rem',
                fontWeight: 700,
                lineHeight: '24px',
                letterSpacing: '0.2px',
                textAlign: 'center',
                color: '#1A202C',
                marginTop: '1rem',
              }}
            >
              You don't have any templates
            </Typography>
          </Box>
        )}

        {templates.map(template => (
          <TemplateRow
            key={template._id}
            template={template}
            setTemplates={setTemplates}
            templates={templates}
          />
        ))}
      </Box>
    </Box>
  );
}

function SliderButton({ isActive, onToggle }) {
  return (
    <ThemeProvider theme={theme}>
      <Button
        onClick={onToggle}
        aria-pressed={isActive}
        sx={{
          position: 'relative',
          width: '6rem',
          height: '2rem',
          backgroundColor: 'white',
          borderRadius: 32,
          padding: 1,
          boxShadow: 3,
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: 'white',
          },
          '&:focus': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <motion.div
          animate={{
            x: isActive ? '100%' : '0%',
          }}
          transition={{
            type: 'spring',
            stiffness: 700,
            damping: 30,
          }}
          style={{
            position: 'absolute',
            left: 4,
            top: 4,
            width: 'calc(50% - 4px)',
            height: 'calc(100% - 8px)',
            borderRadius: 28,
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        />
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            gap: '1rem',
            px: 3,
          }}
        >
          <Box
            sx={{
              color: isActive ? 'text.secondary' : 'white',
              transition: 'color 0.3s',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <GlobeLock />
          </Box>
          <Box
            sx={{
              color: isActive ? 'white' : 'text.secondary',
              transition: 'color 0.3s',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Globe />
          </Box>
        </Box>
      </Button>
    </ThemeProvider>
  );
}

function TemplateRow({ template, setTemplates, templates }) {
  const { loadTemplate, setEditing, getAccessToken } = useContext(
    ReportTemplatesContext,
  );
  const styles = {
    buttonStyle: {
      border: '1px solid',
      borderRadius: '0.5rem',
      width: '5.5rem',
      fontFamily: 'Lato',
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: '21px',
      letterSpacing: '0.2px',
      textAlign: 'right',
    },
  };

  async function deleteTemplate(templateID) {
    try {
      const url = process.env.REACT_APP_NODE_URL + '/templates/' + templateID;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      if (!res.ok) throw new Error('Error in Deleting Template Endpoint');

      const updatedTemplates = templates.filter(
        template => template._id !== templateID,
      );
      setTemplates(updatedTemplates);
    } catch (error) {
      console.error(error);
    }
  }

  async function toggleVisibility(templateID, isDefault) {
    try {
      const url = process.env.REACT_APP_NODE_URL + '/templates/make-default';
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ templateID, isDefault }),
      });

      if (!res.ok) throw new Error('Error in Toggling Visibility');
      const updatedTemplates = templates.map(template =>
        template._id === templateID ? { ...template, isDefault } : template,
      );
      setTemplates(updatedTemplates);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '3.6rem',
        borderTop: '1px solid #EDF2F7',
      }}
    >
      {/* Title */}
      <Box
        sx={[
          {
            fontFamily: 'Lato',
            fontSize: '1rem',
            padding: '0.375rem 0.5rem 0.375rem 0.5rem',
            borderRadius: '0.5rem',
          },

          template?.theme
            ? template.theme
            : getColor(Math.floor(Math.random() * 5)),
        ]}
      >
        {template.templateName}
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2.2rem',
        }}
      >
        <Button
          sx={[
            styles.buttonStyle,
            {
              borderColor: '#00485259',
              color: '#1A202C',
            },
          ]}
          onClick={async () => {
            setEditing(true);
            loadTemplate(template);
          }}
        >
          Edit
        </Button>
        <Button
          sx={[
            styles.buttonStyle,
            {
              borderColor: '#FF474759',
              color: '#FF4747',
            },
          ]}
          onClick={() => {
            deleteTemplate(template._id);
          }}
        >
          Delete
        </Button>
        <SliderButton
          isActive={template.isDefault}
          onToggle={() => toggleVisibility(template._id, !template.isDefault)}
        />
      </Box>
    </Box>
  );
}
